import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, RotateCcw } from "lucide-react";

// UI Components
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import { Label } from "@shared/components/ui/label";
import { Textarea } from "@shared/components/ui/textarea";

// Hooks
import {
  useUpdateTicketMutation,
  useResolveTicketMutation,
  useReopenTicketMutation,
} from "./hooks/useTickets.js";

// Constants
import { TICKET_STATUS_COLORS } from "@shared/utils/constants";

const TicketManagementActions = ({ ticket, onUpdate }) => {
  const { t } = useTranslation();
  const [resolution, setResolution] = useState("");
  const [showResolveDialog, setShowResolveDialog] = useState(false);

  // Mutations
  const updateTicketMutation = useUpdateTicketMutation();
  const resolveTicketMutation = useResolveTicketMutation();
  const reopenTicketMutation = useReopenTicketMutation();

  const getStatusColor = (status) => {
    return (
      TICKET_STATUS_COLORS[status] ||
      "bg-gray-100 text-gray-800 border-gray-200"
    );
  };

  const handleStatusChange = async (newStatus) => {
    await updateTicketMutation.mutateAsync({
      id: ticket._id,
      updateData: { status: newStatus },
    });
    onUpdate?.();
  };

  const handleResolveTicket = async () => {
    await resolveTicketMutation.mutateAsync({
      id: ticket._id,
      resolution: resolution || "Ticket resolved by admin",
    });

    setShowResolveDialog(false);
    setResolution("");
    onUpdate?.();
  };

  const handleReopenTicket = async () => {
    await reopenTicketMutation.mutateAsync(ticket._id);
    onUpdate?.();
  };

  return (
    <div className="space-y-4">
      {/* Current Status Display */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">
            {t("common.status", "Status")}:
          </span>
          <Badge className={getStatusColor(ticket.status)}>
            {t(`tickets.status.${ticket.status}`, ticket.status)}
          </Badge>
        </div>
      </div>

      {/* Status Management */}
      {ticket.status !== "closed" && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-2">
            {/* Status Change */}
            <div>
              <Label className="text-sm">
                {t("tickets.changeStatus", "Change Status")}
              </Label>
              <Select onValueChange={handleStatusChange}>
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={t("tickets.selectStatus", "Select status")}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">
                    {t("tickets.status.open", "Open")}
                  </SelectItem>
                  <SelectItem value="in_progress">
                    {t("tickets.status.in_progress", "In Progress")}
                  </SelectItem>
                  <SelectItem value="waiting_for_response">
                    {t(
                      "tickets.status.waiting_for_response",
                      "Waiting for Response"
                    )}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            {/* Resolve Ticket */}
            {ticket.status !== "resolved" && (
              <Dialog
                open={showResolveDialog}
                onOpenChange={setShowResolveDialog}
              >
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {t("tickets.resolve", "Resolve")}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {t("tickets.resolveTicket", "Resolve Ticket")}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="resolution">
                        {t("tickets.resolution", "Resolution")}
                      </Label>
                      <Textarea
                        id="resolution"
                        value={resolution}
                        onChange={(e) => setResolution(e.target.value)}
                        placeholder={t(
                          "tickets.resolutionPlaceholder",
                          "Describe how this issue was resolved..."
                        )}
                        className="mt-2"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        onClick={() => setShowResolveDialog(false)}
                      >
                        {t("common.cancel", "Cancel")}
                      </Button>
                      <Button
                        onClick={handleResolveTicket}
                        disabled={resolveTicketMutation.isPending}
                      >
                        {resolveTicketMutation.isPending
                          ? t("common.resolving", "Resolving...")
                          : t("tickets.resolve", "Resolve")}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      )}

      {/* Reopen Ticket (if closed) */}
      {ticket.status === "closed" && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleReopenTicket}
          disabled={reopenTicketMutation.isPending}
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          {reopenTicketMutation.isPending
            ? t("common.reopening", "Reopening...")
            : t("tickets.reopen", "Reopen")}
        </Button>
      )}
    </div>
  );
};

export default TicketManagementActions;
